import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import closeIcon from '../../assets/closeIcon.png';
import Link from '../Link';
import SvgIcon from '../SvgIcon';
import './AdjustableModal.scss';
import useDeviceType from '../../hooks/useDeviceType';
import { isRightToLeftDirection } from '../../util/utils';

const AdjustableModal = ({
    children,
    onClose,
    defaultModalWidth = 540,
    defaultModalHeight = 344,
    minConstraints = [193, 130], // Minimum allowed dimensions [width, height]
    maxConstraints = [714, 435], // Maximum allowed dimensions [width, height]
}) => {
    const { dimensions } = useDeviceType(); // Get current device screen dimensions

    // State for modal size (width and height)
    const [modalSize, setModalSize] = useState({
        width: defaultModalWidth,
        height: defaultModalHeight,
    });

    const [position, setPosition] = useState(null);

    // Set initial modal position to center of the screen based on current dimensions
    useEffect(() => {
        if (dimensions.width && dimensions.height) {
            setPosition({
                x: dimensions.width / 2 - defaultModalWidth / 2,
                y: dimensions.height / 2 - defaultModalHeight / 2,
            });
        }
    }, [dimensions.width, dimensions.height, isRightToLeftDirection]);

    // Handle resize and update modal size and position if RTL
    const resizeHandler = (e, { size }) => {
        const widthDiff = size.width - modalSize.width;

        setModalSize({ width: size.width, height: size.height });

        // Adjust x-position if in RTL layout
        if (isRightToLeftDirection) {
            setPosition((prev) => ({
                ...prev,
                x: prev.x - widthDiff,
            }));
        }
    };

    return (
        <React.Fragment>
            {position && (
                <Draggable
                    bounds={{
                        left: 0,
                        top: 0,
                        right: dimensions.width - modalSize.width,
                        bottom: dimensions.height - modalSize.height,
                    }} // Limit modal dragging within viewport
                    handle=".handle" // Only allow dragging from the handle element
                    position={position}
                    onStop={(e, data) => setPosition({ x: data.x, y: data.y })} // Update position on drag stop
                    defaultClassName="adjustable-modal-draggable"
                >
                    <ResizableBox
                        width={modalSize.width}
                        height={modalSize.height}
                        minConstraints={minConstraints}
                        maxConstraints={maxConstraints}
                        onResize={resizeHandler}
                        resizeHandles={isRightToLeftDirection ? ['sw'] : ['se']} // Handle position depends on direction
                        handle={
                            <div
                                className={`resize-handle ${
                                    isRightToLeftDirection
                                        ? 'resize-handle-rtl'
                                        : ''
                                }`}
                            >
                                <div>
                                    <SvgIcon name="Resize_BLUE" />
                                </div>
                            </div>
                        }
                    >
                        <div className="adjustable-draggable-container">
                            {/* Drag Handle UI (3 dots) */}
                            <div className="handle">
                                <div className="dot"></div>
                                <div className="dot"></div>
                                <div className="dot"></div>
                            </div>

                            {/* Close Button */}
                            <div
                                className={`__close ${
                                    isRightToLeftDirection ? '__close-rtl' : ''
                                }`}
                            >
                                <Link onClick={onClose}>
                                    <img
                                        className="__profile-images"
                                        src={closeIcon}
                                        alt="close"
                                    />
                                </Link>
                            </div>

                            {/* Modal Content */}
                            <div>{children}</div>
                        </div>
                    </ResizableBox>
                </Draggable>
            )}
        </React.Fragment>
    );
};

AdjustableModal.propTypes = {
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func.isRequired,
    defaultModalWidth: PropTypes.number,
    defaultModalHeight: PropTypes.number,
    minConstraints: PropTypes.arrayOf(PropTypes.number),
    maxConstraints: PropTypes.arrayOf(PropTypes.number),
};

export default AdjustableModal;
